import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Save, Loader2, Clock, FileText, AlertTriangle, Trash2, PauseCircle, PlayCircle, MessageSquare, ChevronRight, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import EmergencySettingsCard from '../components/EmergencySettingsCard';
import ProviderTypesCard from '../components/ProviderTypesCard';
import GalleryCard from '../components/GalleryCard';
import ProviderDocumentsCard from '../components/ProviderDocumentsCard';
import { resetTour } from '../lib/tutorial';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { token, logout, user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Account status
  const [accountStatus, setAccountStatus] = useState('PENDING');
  const [canDelete, setCanDelete] = useState(true);
  const [activeBookings, setActiveBookings] = useState(0);
  
  // Cancellation policy settings
  const [cancellationWindowHours, setCancellationWindowHours] = useState('');
  const [cancellationPolicyText, setCancellationPolicyText] = useState('');
  const [useCustomPolicy, setUseCustomPolicy] = useState(false);
  
  // Dialogs
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadSettings();
    checkCanDelete();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/dashboard/settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to load settings');
      
      const data = await response.json();
      setCancellationWindowHours(data.cancellationWindowHours?.toString() || '');
      setCancellationPolicyText(data.cancellationPolicyText || '');
      setUseCustomPolicy(!!data.cancellationPolicyText);
      setAccountStatus(data.accountStatus || 'PENDING');
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const checkCanDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/account/can-delete`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCanDelete(data.canDelete);
        setActiveBookings(data.activeBookings);
      }
    } catch (error) {
      console.error('Error checking delete status:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const settingsData = {
        cancellationWindowHours: cancellationWindowHours ? parseInt(cancellationWindowHours) : null,
        cancellationPolicyText: useCustomPolicy ? cancellationPolicyText : null
      };
      
      const response = await fetch(`${API_URL}/api/dashboard/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(settingsData),
      });
      
      if (!response.ok) throw new Error('Failed to save settings');
      
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      setActionLoading(true);
      
      const response = await fetch(`${API_URL}/api/dashboard/account/deactivate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to deactivate account');
      }
      
      toast.success('Account deactivated. You can reactivate anytime from this page.');
      setAccountStatus('DEACTIVATED');
      setDeactivateDialogOpen(false);
    } catch (error) {
      console.error('Error deactivating account:', error);
      toast.error(error.message || 'Failed to deactivate account');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      
      const response = await fetch(`${API_URL}/api/dashboard/account/reactivate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to reactivate account');
      }
      
      toast.success('Account reactivated! You can now receive bookings.');
      setAccountStatus('ACTIVE');
    } catch (error) {
      console.error('Error reactivating account:', error);
      toast.error(error.message || 'Failed to reactivate account');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      
      const response = await fetch(`${API_URL}/api/dashboard/account`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete account');
      }
      
      toast.success('Account deleted successfully');
      setDeleteDialogOpen(false);
      logout();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.message || 'Failed to delete account');
    } finally {
      setActionLoading(false);
    }
  };

  const presetWindows = [
    { value: 'none', label: 'No cancellation policy' },
    { value: '12', label: '12 hours' },
    { value: '24', label: '24 hours' },
    { value: '48', label: '48 hours' },
    { value: '72', label: '72 hours (3 days)' },
    { value: 'custom', label: 'Custom hours' },
  ];

  const [showCustomInput, setShowCustomInput] = useState(false);

  const handleWindowChange = (value) => {
    if (value === 'custom') {
      setShowCustomInput(true);
      setCancellationWindowHours('');
    } else if (value === 'none') {
      setShowCustomInput(false);
      setCancellationWindowHours('');
    } else {
      setShowCustomInput(false);
      setCancellationWindowHours(value);
    }
  };

  const getCurrentSelectValue = () => {
    if (showCustomInput) return 'custom';
    if (!cancellationWindowHours) return 'none';
    if (['12', '24', '48', '72'].includes(cancellationWindowHours)) {
      return cancellationWindowHours;
    }
    return 'custom';
  };

  const getStatusBadge = () => {
    const variants = {
      ACTIVE: 'bg-green-100 text-green-800 border-green-300',
      PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      DEACTIVATED: 'bg-red-100 text-red-800 border-red-300',
    };
    
    return (
      <span className={`px-2 py-1 text-sm font-medium rounded border ${variants[accountStatus] || variants.PENDING}`}>
        {accountStatus || 'PENDING'}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="settings-page-title">Settings</h1>
          <p className="text-muted-foreground">Configure your booking policies and manage your account</p>
        </div>

        {/* Help & Tutorial — replay the guided tour */}
        <Card data-testid="help-tutorial-card">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <HelpCircle className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Help &amp; Tutorial</h3>
                  <p className="text-sm text-muted-foreground">
                    New here, or need a refresher? Replay the quick tour of how RanchWorks works.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  resetTour(user?.id);
                  toast.success('Tour will start on your dashboard');
                  navigate('/dashboard');
                }}
                data-testid="replay-tour-button"
              >
                <PlayCircle className="mr-2 h-4 w-4" />
                Show tour again
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Provider Types (RW-FIX-003) — multi-select offered service types */}
        <ProviderTypesCard />

        <GalleryCard />

        <ProviderDocumentsCard />

        {/* Consultation Settings Link */}
        <Card 
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => navigate('/dashboard/consultation-settings')}
          data-testid="consultation-settings-card"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <MessageSquare className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Consultation Settings</h3>
                  <p className="text-sm text-muted-foreground">
                    Configure text, audio, and video consultations
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        {/* Account Management Section */}
        <Card data-testid="account-management-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Account Management
            </CardTitle>
            <CardDescription>
              Manage your account status and data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Account Status */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Account Status</p>
                <p className="text-sm text-muted-foreground">
                  {accountStatus === 'ACTIVE' && 'Your account is active and can receive bookings'}
                  {accountStatus === 'PENDING' && 'Your account is pending activation'}
                  {accountStatus === 'DEACTIVATED' && 'Your account is deactivated (read-only)'}
                </p>
              </div>
              {getStatusBadge()}
            </div>

            {/* Deactivate / Reactivate */}
            {accountStatus === 'DEACTIVATED' ? (
              <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="font-medium text-green-900">Reactivate Your Account</p>
                    <p className="text-sm text-green-700">
                      Your account is currently deactivated. Reactivate to start receiving bookings again.
                    </p>
                  </div>
                  <Button
                    onClick={handleReactivate}
                    disabled={actionLoading}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="reactivate-button"
                  >
                    {actionLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <PlayCircle className="mr-2 h-4 w-4" />
                        Reactivate Account
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : accountStatus === 'ACTIVE' && (
              <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="font-medium text-amber-900">Deactivate Account</p>
                    <p className="text-sm text-amber-700">
                      Temporarily deactivate your account. You'll still have read-only access to view your data.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setDeactivateDialogOpen(true)}
                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    data-testid="deactivate-button"
                  >
                    <PauseCircle className="mr-2 h-4 w-4" />
                    Deactivate Account
                  </Button>
                </div>
              </div>
            )}

            {/* Delete Account */}
            <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="font-medium text-red-900">Delete Account</p>
                  <p className="text-sm text-red-700">
                    Permanently delete your account and all associated data. This cannot be undone.
                  </p>
                  {!canDelete && (
                    <p className="text-sm text-red-800 mt-2 font-medium">
                      You have {activeBookings} active booking(s). Complete or cancel them before deleting.
                    </p>
                  )}
                </div>
                <Button
                  variant="destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={!canDelete}
                  data-testid="delete-account-button"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Account
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cancellation Policy Section */}
        <EmergencySettingsCard />

        <Card data-testid="cancellation-policy-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Cancellation Policy
            </CardTitle>
            <CardDescription>
              Set your cancellation window to protect against last-minute cancellations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Cancellation Window */}
            <div className="space-y-3">
              <Label htmlFor="cancellationWindow">Cancellation Window</Label>
              <Select 
                value={getCurrentSelectValue()} 
                onValueChange={handleWindowChange}
              >
                <SelectTrigger id="cancellationWindow" data-testid="cancellation-window-select">
                  <SelectValue placeholder="Select cancellation window" />
                </SelectTrigger>
                <SelectContent>
                  {presetWindows.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {showCustomInput && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="168"
                    placeholder="Enter hours"
                    value={cancellationWindowHours}
                    onChange={(e) => setCancellationWindowHours(e.target.value)}
                    className="w-32"
                    data-testid="custom-hours-input"
                  />
                  <span className="text-sm text-muted-foreground">hours</span>
                </div>
              )}
              
              {cancellationWindowHours && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      Clients will see: "Appointments canceled within {cancellationWindowHours} hours may result in loss of deposit."
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Custom Policy Text */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="useCustomPolicy"
                  checked={useCustomPolicy}
                  onChange={(e) => setUseCustomPolicy(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                  data-testid="custom-policy-checkbox"
                />
                <Label htmlFor="useCustomPolicy" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Add custom policy text
                </Label>
              </div>
              
              {useCustomPolicy && (
                <div className="space-y-2">
                  <Textarea
                    placeholder="Enter your custom cancellation policy..."
                    value={cancellationPolicyText}
                    onChange={(e) => setCancellationPolicyText(e.target.value)}
                    rows={4}
                    data-testid="custom-policy-text"
                  />
                  <p className="text-xs text-muted-foreground">
                    This custom text will be displayed to clients in addition to the standard cancellation window notice.
                  </p>
                </div>
              )}
            </div>

            {/* Preview */}
            {(cancellationWindowHours || (useCustomPolicy && cancellationPolicyText)) && (
              <div className="border-t pt-4">
                <Label className="mb-2 block">Preview (how clients will see it):</Label>
                <div className="p-4 bg-muted rounded-lg space-y-2" data-testid="policy-preview">
                  <p className="font-semibold text-sm">Cancellation Policy:</p>
                  {cancellationWindowHours && (
                    <p className="text-sm text-muted-foreground">
                      Appointments canceled within {cancellationWindowHours} hours may result in loss of deposit.
                    </p>
                  )}
                  {useCustomPolicy && cancellationPolicyText && (
                    <p className="text-sm text-muted-foreground">{cancellationPolicyText}</p>
                  )}
                </div>
              </div>
            )}

            {/* Save Button */}
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full sm:w-auto"
              data-testid="save-settings-button"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Settings
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <PauseCircle className="h-5 w-5 text-amber-600" />
              Deactivate Your Account
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate your account?
              <br /><br />
              <strong>What happens when you deactivate:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>You won't receive new booking requests</li>
                <li>Your public profile will show as unavailable</li>
                <li>You'll still have read-only access to your dashboard</li>
                <li>Your data will be preserved</li>
                <li>You can reactivate at any time</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivate}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Deactivate Account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Your Account
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete your account?
              <br /><br />
              <strong>This will permanently delete:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Your provider profile and settings</li>
                <li>All your services and availability</li>
                <li>All your booking history</li>
                <li>All reviews and replies</li>
                <li>All client notes and horse records</li>
              </ul>
              <br />
              <strong className="text-destructive">This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
